import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider, useApp } from './contexts/AppContext';
import LoginScreen from './components/auth/LoginScreen';
import Sidebar from './components/layout/Sidebar';
import MobileBottomNavigation from './components/layout/MobileBottomNavigation';
import TopBar from './components/layout/TopBar';
import QuickCreateMenu from './components/actions/QuickCreateMenu';
import Dashboard from './components/dashboard/Dashboard';
import TicketTable from './components/tickets/TicketTable';
import KanbanBoard from './components/tickets/KanbanBoard';
import TicketDetail from './components/tickets/TicketDetail';
import RoadmapView from './components/roadmap/RoadmapView';
import SettingsView from './components/settings/SettingsView';
import HelpCenter from './components/help/HelpCenter';
import { ENABLE_ROADMAP, TICKET_TYPES, SEVERITIES } from './config/constants';
import pocketbaseService from './services/pocketbase';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import './App.css';

const { FiX, FiPaperclip, FiInbox } = FiIcons;

const CreateTicketModal = ({ onClose, onSuccess }) => {
  const { apps, showToast } = useApp();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    app: '',
    type: 'Bug Report',
    subject: '',
    description: '',
    severity: 'Medium',
    assignedTo: '',
    requesterName: '',
    requesterEmail: '',
    screenshots: [],
  });

  const activeApps = apps.filter((app) => app.isActive !== false);

  const handleFileChange = (event) => {
    if (!event.target.files) return;
    const filesArray = Array.from(event.target.files);
    if (formData.screenshots.length + filesArray.length > 5) {
      showToast('Maximum 5 screenshots allowed', 'error');
      return;
    }
    setFormData((current) => ({ ...current, screenshots: [...current.screenshots, ...filesArray] }));
  };

  const removeFile = (index) => {
    setFormData((current) => ({ ...current, screenshots: current.screenshots.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.app || !formData.subject || !formData.description) return;

    setLoading(true);
    try {
      const appRecord = activeApps.find((app) => app.id === formData.app);
      const ticketData = {
        ...formData,
        app: formData.app,
        tenantId: appRecord?.appId || 'internal',
        status: 'New',
        createdBy: 'Agent',
        authorType: 'Agent',
        source: 'Internal',
      };

      const createdTicket = await pocketbaseService.createTicket(ticketData);
      await pocketbaseService.createTicketLog({
        ticket: createdTicket.id,
        authorType: 'Agent',
        authorName: user?.name || user?.email || 'Agent',
        authorEmail: user?.email || '',
        message: 'Ticket created internally by support',
        isInternal: true,
      });

      showToast(`Ticket created: ${createdTicket.ticketRef}`);
      onSuccess(createdTicket);
    } catch (error) {
      console.error('Failed to create ticket:', error);
      showToast(error.message || 'Failed to create ticket', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-overlay flex items-center justify-center z-[100] p-2 sm:p-4">
      <motion.section
        initial={{ scale: 0.97, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="bg-surface rounded-2xl w-full max-w-3xl max-h-[94vh] shadow-2xl border border-border flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Create new ticket"
      >
        <header className="px-5 sm:px-6 py-4 border-b border-border flex justify-between items-start gap-4 bg-surface shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center shrink-0"><SafeIcon icon={FiInbox} className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">Manual support entry</p>
              <h2 className="text-xl font-bold text-text mt-0.5">Create New Ticket</h2>
              <p className="text-sm text-muted mt-1">Log a request received by phone, email or internally.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={loading} className="p-2 rounded-xl text-muted hover:text-text hover:bg-background" aria-label="Close"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
        </header>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-5 sm:p-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="App" required>
                <select required value={formData.app} onChange={(event) => setFormData({ ...formData, app: event.target.value })} className="control">
                  <option value="">Select App</option>
                  {activeApps.map((app) => <option key={app.id} value={app.id}>{app.appName}</option>)}
                </select>
              </Field>
              <Field label="Type" required>
                <select required value={formData.type} onChange={(event) => setFormData({ ...formData, type: event.target.value })} className="control">
                  {TICKET_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Subject" required>
              <input type="text" required value={formData.subject} onChange={(event) => setFormData({ ...formData, subject: event.target.value })} className="control" placeholder="Short summary of the issue" />
            </Field>

            <Field label="Description" required>
              <textarea required rows={5} value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} className="control resize-y" placeholder="Describe what happened, what was expected and any useful context." />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Severity">
                <select value={formData.severity} onChange={(event) => setFormData({ ...formData, severity: event.target.value })} className="control">
                  {SEVERITIES.map((severity) => <option key={severity} value={severity}>{severity}</option>)}
                </select>
              </Field>
              <Field label="Assigned To">
                <input type="text" value={formData.assignedTo} onChange={(event) => setFormData({ ...formData, assignedTo: event.target.value })} className="control" placeholder="Agent name or email" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Requester Name">
                <input type="text" value={formData.requesterName} onChange={(event) => setFormData({ ...formData, requesterName: event.target.value })} className="control" />
              </Field>
              <Field label="Requester Email">
                <input type="email" value={formData.requesterEmail} onChange={(event) => setFormData({ ...formData, requesterEmail: event.target.value })} className="control" />
              </Field>
            </div>

            <Field label="Screenshots" hint={`${formData.screenshots.length}/5 selected`}>
              <div className="border-2 border-dashed border-border rounded-2xl p-5 bg-background">
                <input type="file" multiple accept="image/*" onChange={handleFileChange} className="hidden" id="ticket-file-upload" />
                <label htmlFor="ticket-file-upload" className="cursor-pointer flex flex-col items-center justify-center text-muted hover:text-primary min-h-24 text-center">
                  <SafeIcon icon={FiPaperclip} className="w-7 h-7 mb-2" />
                  <span className="font-semibold text-sm">Upload screenshots</span>
                  <span className="text-xs mt-1">Maximum 5 images</span>
                </label>
              </div>
              {formData.screenshots.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData.screenshots.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-3 text-sm bg-background border border-border p-3 rounded-xl">
                      <span className="truncate">{file.name}</span>
                      <button type="button" onClick={() => removeFile(index)} className="p-2 rounded-lg text-muted hover:text-primary hover:bg-primary-soft" aria-label={`Remove ${file.name}`}><SafeIcon icon={FiX} className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </Field>
          </div>

          <footer className="sticky bottom-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 sm:px-6 py-4 border-t border-border bg-surface">
            <button type="button" onClick={onClose} disabled={loading} className="min-h-11 px-4 py-2.5 text-text border border-border rounded-xl hover:bg-background font-semibold">Cancel</button>
            <button type="submit" disabled={loading} className="min-h-11 px-6 py-2.5 bg-primary text-surface rounded-xl hover:bg-primary-hover disabled:opacity-50 font-semibold">{loading ? 'Creating...' : 'Create Ticket'}</button>
          </footer>
        </form>
      </motion.section>
    </div>
  );
};

const Field = ({ label, required, hint, children }) => (
  <label className="block">
    <span className="flex items-center justify-between gap-2 text-sm font-semibold text-text mb-1.5">
      <span>{label}{required && <span className="text-primary"> *</span>}</span>
      {hint && <span className="text-xs font-normal text-muted">{hint}</span>}
    </span>
    {children}
  </label>
);

const AppContent = () => {
  const { isAuthenticated, loading, logout } = useAuth();
  const {
    isCreateTicketModalOpen,
    openCreateTicketModal,
    closeCreateTicketModal,
    setSelectedTicket,
    setInboxPreset,
  } = useApp();
  const [activeView, setActiveView] = useState('inbox');
  const [selectedTicketLocal, setSelectedTicketLocal] = useState(null);
  const [ticketOpenOptions, setTicketOpenOptions] = useState({ edit: false, tab: 'overview' });
  const [refreshKey, setRefreshKey] = useState(0);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!isAuthenticated) return <LoginScreen />;

  const handleRefresh = () => setRefreshKey((current) => current + 1);
  const handleTicketSelect = (ticket, options = {}) => {
    setTicketOpenOptions({ edit: Boolean(options.edit), tab: options.tab || 'overview' });
    setSelectedTicketLocal(ticket);
    setSelectedTicket(ticket);
  };
  const handleDashboardNavigate = (preset = {}) => { setInboxPreset(preset); setActiveView('inbox'); };
  const handleCloseTicket = () => {
    setSelectedTicketLocal(null);
    setSelectedTicket(null);
    setTicketOpenOptions({ edit: false, tab: 'overview' });
    handleRefresh();
  };
  const handleTicketCreated = (newTicket) => {
    closeCreateTicketModal();
    handleRefresh();
    pocketbaseService.getTicket(newTicket.id).then((fullTicket) => {
      setSelectedTicketLocal(fullTicket);
      setSelectedTicket(fullTicket);
    });
  };

  const renderMainContent = () => {
    switch (activeView) {
      case 'dashboard': return <Dashboard refreshKey={refreshKey} onNavigateToInbox={handleDashboardNavigate} onTicketSelect={handleTicketSelect} />;
      case 'inbox': return <TicketTable key="inbox" refreshKey={refreshKey} onTicketSelect={handleTicketSelect} />;
      case 'kanban': return <KanbanBoard key="kanban" refreshKey={refreshKey} onTicketSelect={handleTicketSelect} />;
      case 'roadmap': return ENABLE_ROADMAP ? <RoadmapView key="roadmap" refreshKey={refreshKey} onTicketSelect={handleTicketSelect} /> : null;
      case 'help': return <HelpCenter onViewChange={setActiveView} />;
      case 'admin': return <SettingsView adminMode onViewChange={setActiveView} />;
      case 'settings': return <SettingsView adminMode={false} />;
      default: return <TicketTable key="default" refreshKey={refreshKey} onTicketSelect={handleTicketSelect} />;
    }
  };

  return (
    <div className="support-shell h-screen flex bg-background">
      <Sidebar activeView={activeView} onViewChange={setActiveView} onLogout={logout} />
      <div className="support-content flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar onRefresh={handleRefresh} />
        <main className="support-main flex-1 overflow-auto p-4 sm:p-5 lg:p-6">{renderMainContent()}</main>
      </div>

      <QuickCreateMenu
        open={quickCreateOpen}
        onOpen={() => setQuickCreateOpen(true)}
        onClose={() => setQuickCreateOpen(false)}
        onCreateTicket={openCreateTicketModal}
        onOpenHelp={() => setActiveView('help')}
      />

      <MobileBottomNavigation
        activeView={activeView}
        onViewChange={setActiveView}
        onCreate={() => setQuickCreateOpen(true)}
        onLogout={logout}
      />

      {selectedTicketLocal && <TicketDetail ticket={selectedTicketLocal} onClose={handleCloseTicket} initialEditMode={ticketOpenOptions.edit} initialTab={ticketOpenOptions.tab} />}
      {isCreateTicketModalOpen && <CreateTicketModal onClose={closeCreateTicketModal} onSuccess={handleTicketCreated} />}
    </div>
  );
};

function App() {
  return <AuthProvider><AppProvider><AppContent /></AppProvider></AuthProvider>;
}

export default App;